import { Link, useParams } from 'react-router'
import { Alert, AlertDescription, AlertTitle } from '@klear/ui/components/alert'
import { useTraderExternalBankAccounts } from '@klear/ui/hooks/use-trader-external-bank-accounts'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { useTrader } from '@klear/ui/hooks/use-trader'
import {
  ROUTE_KLEAR_ACCOUNT,
  ROUTE_MAKE_PAYMENT,
  ROUTE_EXTERNAL_BANK,
  PERMISSION_ENTITY_TREASURY,
  PERMISSION_ACTION_VIEW
} from '@klear/constants'

interface UnverifiedBankAccountBannerProps {
  className?: string
}

export function UnverifiedBankAccountBanner({ className }: UnverifiedBankAccountBannerProps) {
  const { user } = useAuth()
  const { traderId } = useParams()
  const { urlPrefixApp } = useTrader(traderId)
  const { traderExternalBankAccounts } = useTraderExternalBankAccounts()

  const hasUnverifiedAccounts = traderExternalBankAccounts.some((account) => account.needsVerification)

  if (!hasUnverifiedAccounts || !user.hasPermission(PERMISSION_ENTITY_TREASURY, PERMISSION_ACTION_VIEW)) {
    return null
  }

  return (
    <Alert className={`border-yellow-200 bg-yellow-50 dark:border-yellow-900 dark:bg-yellow-950 ${className || ''}`}>
      <AlertTitle className="text-yellow-800 dark:text-yellow-200">
        Action Required: Complete Bank Account Verification
      </AlertTitle>
      <AlertDescription className="text-yellow-700 dark:text-yellow-300">
        <p className="mb-2">
          You have external bank accounts that need verification. To enable transfers from Klear to your external
          accounts:
        </p>
        <ol className="list-decimal list-inside space-y-1 mb-2">
          <li>
            <Link to={`${urlPrefixApp}/${ROUTE_EXTERNAL_BANK}`} className="font-medium underline hover:text-yellow-600">
              Start the verification process
            </Link>{' '}
            to complete microdeposits on all external bank accounts
          </li>
          <li>
            As a temporary workaround, use{' '}
            <Link
              to={`${urlPrefixApp}/${ROUTE_KLEAR_ACCOUNT}/${ROUTE_MAKE_PAYMENT}?useUnverified=true`}
              className="font-medium underline hover:text-yellow-600"
            >
              Make a Payment
            </Link>{' '}
            to send yourself money
          </li>
        </ol>
      </AlertDescription>
    </Alert>
  )
}
